/**
 * Apartsales — /compare (веха v5-2 · X-D3).
 *
 * Сравнение 2–4 юнитов колонками с подсветкой различий: по каждой
 * строке лучшее значение выделяется. Список — из useCompare
 * (localStorage). Данные колонок — fetchUnitOverview по каждому id.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { useCompare } from '~/lib/useCompare';
import { fetchUnitOverview, type UnitOverview } from '~/lib/overview-api';

export const Route = createFileRoute('/compare')({
  component: ComparePage,
});

interface Col {
  id: string;
  ov: UnitOverview | null;
}

type Better = 'high' | 'low' | 'none';

interface Row {
  label: string;
  get: (ov: UnitOverview | null) => number | string | null;
  fmt?: (v: number) => string;
  better: Better; // какое значение лучше для подсветки
}

const fmtMoney = (v: number) =>
  v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1).replace('.', ',')} млн ₽` : `${Math.round(v / 1000)} тыс ₽`;

const ROWS: Row[] = [
  { label: 'Цена', get: (o) => o?.money?.price ?? null, fmt: fmtMoney, better: 'low' },
  { label: 'Цена за м²', get: (o) => o?.money?.price_per_sqm ?? null, fmt: fmtMoney, better: 'low' },
  { label: 'Площадь', get: (o) => o?.unit?.area_total ?? null, fmt: (v) => `${v} м²`, better: 'high' },
  { label: 'Комнат', get: (o) => o?.unit?.rooms ?? null, better: 'none' },
  { label: 'Этаж', get: (o) => o?.unit?.floor ?? null, better: 'none' },
  { label: 'Потолки', get: (o) => o?.unit?.ceiling_height_m ?? null, fmt: (v) => `${v} м`, better: 'high' },
  { label: 'Кухня', get: (o) => o?.unit?.area_kitchen ?? null, fmt: (v) => `${v} м²`, better: 'high' },
  { label: 'Отделка', get: (o) => o?.unit?.finishing ?? null, better: 'none' },
  { label: 'Apartsales Score', get: (o) => o?.score ?? null, better: 'high' },
];

function ComparePage() {
  const navigate = useNavigate();
  const { ids, remove, clear } = useCompare();
  const [cols, setCols] = useState<Col[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all(ids.map((id) => fetchUnitOverview(id).then((ov) => ({ id, ov })))).then((res) => {
      if (cancelled) return;
      setCols(res);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [ids]);

  if (ids.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: 'var(--color-label-3)' }}>
        <div style={{ fontSize: 40, marginBottom: 8 }} aria-hidden>⚖️</div>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-label)', margin: '0 0 6px' }}>
          Сравнение пусто
        </h2>
        <p style={{ fontSize: 14, margin: '0 0 16px' }}>Добавьте до 4 юнитов кнопкой «К сравнению» на карточке.</p>
        <button
          type="button"
          onClick={() => navigate({ to: '/search' })}
          style={{ minHeight: 44, padding: '0 20px', borderRadius: 999, border: 'none', cursor: 'pointer', fontWeight: 700, color: '#fff', background: 'var(--color-brand-blue)' }}
        >
          К поиску
        </button>
      </div>
    );
  }

  // Для подсветки: лучший индекс по каждой числовой строке.
  function bestIndex(row: Row): number | null {
    if (row.better === 'none') return null;
    let best: number | null = null;
    let bestI: number | null = null;
    cols.forEach((c, i) => {
      const v = row.get(c.ov);
      if (typeof v !== 'number') return;
      if (best == null || (row.better === 'high' ? v > best : v < best)) {
        best = v;
        bestI = i;
      }
    });
    return bestI;
  }

  return (
    <div style={{ padding: '16px 12px 40px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--color-label)', margin: 0 }}>
          Сравнение · {ids.length}
        </h1>
        <button
          type="button"
          onClick={clear}
          aria-label="Очистить сравнение"
          style={{ minHeight: 40, padding: '0 14px', fontSize: 13, fontWeight: 600, borderRadius: 999, cursor: 'pointer', color: 'var(--color-label-2)', background: 'var(--color-glass-thin)', border: '0.5px solid var(--color-glass-stroke)' }}
        >
          Очистить
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--color-label-3)' }}>Загрузка…</div>
      ) : (
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%', minWidth: 90 + cols.length * 130 }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, position: 'sticky', left: 0, zIndex: 2, background: 'var(--color-bg, #fff)' }} />
                {cols.map((c) => (
                  <th key={c.id} style={thStyle}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-label)' }}>
                      {c.ov?.unit?.rooms != null ? `${c.ov.unit.rooms}-комн` : 'Юнит'}
                    </div>
                    <button
                      type="button"
                      onClick={() => remove(c.id)}
                      aria-label="Убрать из сравнения"
                      style={{ marginTop: 4, fontSize: 11, color: '#C0392B', background: 'none', border: 'none', cursor: 'pointer' }}
                    >
                      убрать
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row) => {
                const best = bestIndex(row);
                return (
                  <tr key={row.label}>
                    <td style={{ ...labelTd, position: 'sticky', left: 0, zIndex: 1, background: 'var(--color-bg, #fff)' }}>
                      {row.label}
                    </td>
                    {cols.map((c, i) => {
                      const v = row.get(c.ov);
                      const display = v == null ? '—' : typeof v === 'number' && row.fmt ? row.fmt(v) : String(v);
                      const isBest = best === i && best != null;
                      return (
                        <td
                          key={c.id}
                          style={{
                            ...valueTd,
                            color: isBest ? '#0A7D2E' : 'var(--color-label)',
                            fontWeight: isBest ? 800 : 600,
                            background: isBest ? 'rgba(10,125,46,0.06)' : 'transparent',
                          }}
                        >
                          {display}
                          {isBest && <span aria-hidden style={{ marginLeft: 4 }}>★</span>}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p style={{ margin: 0, fontSize: 11, color: 'var(--color-label-3)', lineHeight: 1.5 }}>
        ★ — лучшее значение по строке. Цена и цена за м² «лучше» — когда ниже;
        площадь, потолки, кухня, score — когда выше.
      </p>
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '10px 12px',
  textAlign: 'left',
  borderBottom: '1px solid var(--color-glass-stroke)',
  verticalAlign: 'top',
};
const labelTd: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: 12.5,
  color: 'var(--color-label-2)',
  borderBottom: '0.5px solid var(--color-glass-stroke)',
  whiteSpace: 'nowrap',
};
const valueTd: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: 13.5,
  borderBottom: '0.5px solid var(--color-glass-stroke)',
  whiteSpace: 'nowrap',
};
